import React, { useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { isNullish } from 'shared/utils/type-utils';
import { DutchieLoyaltyWidget } from 'src/components/widgets/dutchie-loyalty-widget';
import { useLoyalty } from 'src/components/widgets/loyalty-tiers/use-loyalty';
import type {
  GetDutchiePosLoyaltyConfigQuery,
  RewardsActivity,
  RewardV2,
  FindOrCreateReferralCodeMutation,
} from 'src/gql/graphql';
import { DposMyReferral } from './dpos-referrals';
import { DposMyRewardsList } from './dpos-my-rewards-list';
import { DposRewardsActivityList } from './dpos-rewards-activity-list';
import { DPOSRewardsTabs } from './dpos-rewards-tabs';
import { RewardsContainer } from './dpos-rewards-tabs.styles';
import { getDefaultTab, RedemptionType, TabOption, TabValue } from './dpos-utils';

type DposRewardsProps = {
  dposConfigData?: GetDutchiePosLoyaltyConfigQuery['getDutchiePosLoyaltyConfig'];
  rewards: Array<RewardV2>;
  rewardsActivity?: Array<RewardsActivity>;
  referral?: FindOrCreateReferralCodeMutation['findOrCreateReferralCode'];
  isRevokedFromReferralProgram?: boolean;
};

export const DposRewards = ({
  dposConfigData,
  rewards,
  rewardsActivity,
  referral,
  isRevokedFromReferralProgram,
}: DposRewardsProps): JSX.Element => {
  const { onlineDescription, redemptionType, tiers, referralConfig } = dposConfigData ?? {};
  const hideDetailsTab = !onlineDescription?.length || isNullish(onlineDescription);
  const flags = useFlags();
  const isShowReferralsEnabled = flags['pos.customers.refer-a-friend.rollout'] ?? false;
  const { formatValueForDisplay, isDiscountRedemption } = useLoyalty();

  const hasRewardsActivity = rewardsActivity && rewardsActivity.length > 0;

  const [selectedTab, setSelectedTab] = useState(
    hideDetailsTab ? getDefaultTab(redemptionType, hasRewardsActivity, isShowReferralsEnabled) : TabValue.details
  );

  const handleTabChange = (tab: TabValue): void => {
    setSelectedTab(tab);
  };

  const isPoints = isDiscountRedemption;
  const hasTiers = (tiers?.length ?? 0) > 0;
  const showRewardsTab = redemptionType?.toLowerCase() === RedemptionType.discount;
  const showReferralsTab = !isRevokedFromReferralProgram && isShowReferralsEnabled && referralConfig?.isEnabled;

  const tabOptions = [
    { value: TabValue.details, label: 'Details', data: <p>{onlineDescription}</p>, display: !hideDetailsTab },
    {
      value: TabValue.rewards,
      label: 'My Rewards',
      data: showRewardsTab ? <DposMyRewardsList rewards={rewards} /> : null,
      display: showRewardsTab,
    },
    {
      value: TabValue.activity,
      label: 'Activity',
      data: (
        <DposRewardsActivityList
          rewardsActivity={rewardsActivity ?? []}
          isPoints={isPoints}
          formatValueForDisplay={formatValueForDisplay}
        />
      ),
      display: true,
    },
    {
      value: TabValue.referrals,
      label: 'Referrals',
      data: showReferralsTab ? (
        <DposMyReferral referral={referral ?? {}} referralConfiguration={referralConfig} />
      ) : null,
      display: showReferralsTab,
    },
  ] as TabOption[];

  return (
    <RewardsContainer>
      <DutchieLoyaltyWidget
        hasTiers={hasTiers}
        redemptionType={redemptionType ?? ''}
        isWidgetLink={false}
        locationMargin='0px auto 16px'
        displayCTA={false}
      />

      <DPOSRewardsTabs
        selectedTab={selectedTab}
        onTabChange={(tab) => handleTabChange(tab as TabValue)}
        tabOptions={tabOptions}
      >
        {tabOptions.find((tab) => tab.value === selectedTab)?.data}
      </DPOSRewardsTabs>
    </RewardsContainer>
  );
};
